'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Search } from 'lucide-react';
import { fetchResultadosPublicos, type ResultadoPublicoApi } from '@/services/indicadores';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

const ALL_CATEGORIES = [
  'Todas', 'Economía', 'Educación', 'Energía', 'Ambiente y cambio climático', 'Finanzas',
  'Gobernanza', 'Salud', 'Viviendas', 'Población y condiciones sociales', 'Recreación',
  'Seguridad', 'Residuos sólidos', 'Deporte y cultura', 'Telecomunicaciones', 'Transporte',
  'Agricultura urbana y/o local y seguridad alimentaria', 'Planificación urbana',
  'Aguas residuales', 'Agua'
];

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50, 100];

export default function CatalogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [selectedPeriod, setSelectedPeriod] = useState('Todos');
  const [indicators, setIndicators] = useState<ResultadoPublicoApi[]>([]);
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos(),
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          setIndicators(res);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dynamic indicators catalog', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const displayIndicators = useMemo(
    () =>
      indicators.map((ind, idx) => ({
        rowKey: `${ind.corrida_id}-${ind.codigo_interno || idx}`,
        id: ind.codigo_interno || String(ind.corrida_id),
        codigo: ind.codigo_interno || '',
        title: ind.indicador,
        category: ind.categoria,
        value:
          typeof ind.valor_resultado === 'number'
            ? ind.valor_resultado.toLocaleString('es-AR')
            : ind.valor_resultado,
        unit: ind.unidad_simbolo || ind.unidad_medida,
        period: ind.periodo_referencia
          ? new Date(ind.periodo_referencia).getFullYear().toString()
          : 'S/D',
        generationDate: ind.calculado_at
          ? new Date(ind.calculado_at).toLocaleDateString('es-AR')
          : ind.publicada_at
          ? new Date(ind.publicada_at).toLocaleDateString('es-AR')
          : 'S/D',
      })),
    [indicators]
  );

  const displayCategories = useMemo(() => {
    if (categories.length > 0) {
      return ['Todas', ...categories.map((c) => c.nombre)];
    }
    return ALL_CATEGORIES;
  }, [categories]);

  const displayPeriods = useMemo(() => {
    const uniquePeriods = Array.from(
      new Set(
        indicators
          .map((ind) =>
            ind.periodo_referencia
              ? new Date(ind.periodo_referencia).getFullYear().toString()
              : ''
          )
          .filter(Boolean)
      )
    ).sort((a, b) => Number(b) - Number(a));
    return ['Todos', ...uniquePeriods];
  }, [indicators]);

  const filteredIndicators = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return displayIndicators.filter((ind) => {
      const matchesSearch =
        q === '' ||
        ind.title.toLowerCase().includes(q) ||
        ind.codigo.toLowerCase().includes(q) ||
        ind.category.toLowerCase().includes(q);
      const matchesCategory = selectedCategory === 'Todas' || ind.category === selectedCategory;
      const matchesPeriod = selectedPeriod === 'Todos' || ind.period === selectedPeriod;
      return matchesSearch && matchesCategory && matchesPeriod;
    });
  }, [displayIndicators, searchTerm, selectedCategory, selectedPeriod]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedPeriod, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredIndicators.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedIndicators = useMemo(() => {
    const start = (safeCurrentPage - 1) * itemsPerPage;
    return filteredIndicators.slice(start, start + itemsPerPage);
  }, [filteredIndicators, safeCurrentPage, itemsPerPage]);

  const visiblePageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    const start = Math.max(2, safeCurrentPage - 1);
    const end = Math.min(totalPages - 1, safeCurrentPage + 1);
    if (start > 2) pages.push('ellipsis-start');
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < totalPages - 1) pages.push('ellipsis-end');
    pages.push(totalPages);
    return pages;
  }, [totalPages, safeCurrentPage]);

  return (
    <>
      {/* Filter Bar (Sub-header) */}
      <div className="bg-[#F3F4F6] border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex flex-col md:flex-row items-center gap-4 justify-between">
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64 md:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar indicador por nombre o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002B5B] focus:border-transparent text-gray-800"
              />
            </div>

            {/* Category Select */}
            <div className="w-full sm:w-60">
              <SearchableSelect
                value={selectedCategory}
                onChange={(val) => setSelectedCategory(val)}
                options={displayCategories.map((cat) => ({
                  value: cat,
                  label: cat === 'Todas' ? 'Tema: Todas las categorías' : `Tema: ${cat}`,
                }))}
                placeholder="Tema..."
                searchPlaceholder="Buscar tema..."
              />
            </div>

            {/* Period Select */}
            <div className="w-full sm:w-48">
              <SearchableSelect
                value={selectedPeriod}
                onChange={(val) => setSelectedPeriod(val)}
                options={displayPeriods.map((period) => ({
                  value: period,
                  label: period === 'Todos' ? 'Período: Todos' : `Período: ${period}`,
                }))}
                placeholder="Período..."
                searchPlaceholder="Buscar período..."
              />
            </div>
          </div>

          <div className="text-sm text-gray-500 font-semibold whitespace-nowrap">
            {filteredIndicators.length} indicador(es) encontrado(s) (de {displayIndicators.length})
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto w-full px-8 py-10 flex-1">
        <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-xl font-[family-name:var(--font-righteous)] text-[#002B5B] tracking-wide">
              Indicadores Publicados · Período {selectedPeriod === 'Todos' ? 'Histórico' : selectedPeriod}
            </h2>

            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500">
              <span>Registros por página:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-bold text-[#002B5B] outline-none focus:border-[#002B5B]"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Indicador</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Tema</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Valor Vigente</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Unidad</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Período</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Fecha de generación</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-4 bg-gray-200 rounded w-48"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-3 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-3 bg-gray-200 rounded w-16"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-3 bg-gray-200 rounded w-12"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-4 bg-gray-200 rounded-full w-20"></div></td>
                      <td className="px-6 py-4 border-b border-gray-50"><div className="h-6 bg-gray-200 rounded w-12 ml-auto"></div></td>
                    </tr>
                  ))
                ) : paginatedIndicators.length > 0 ? (
                  paginatedIndicators.map((ind) => (
                    <tr key={ind.rowKey} className="hover:bg-[#F8FAFC] transition-colors group">
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-sm font-bold text-[#0053D6]">{ind.title}</div>
                        {ind.codigo && (
                          <div className="text-[11px] font-mono text-gray-400">Cód.: {ind.codigo}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-500">{ind.category}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-sm font-bold text-gray-900">{ind.value}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-500">{ind.unit}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-500">{ind.period}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <span className="inline-flex px-3 py-1 bg-[#EFF6FF] text-[#3B82F6] rounded-full text-[10px] font-bold">
                          {ind.generationDate}
                        </span>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50 text-right">
                        <Link
                          href={`/indicadores/${ind.id}`}
                          className="inline-flex items-center justify-center bg-[#F0F5FA] hover:bg-[#E2E8F0] text-[#0053D6] text-xs font-bold py-1.5 px-4 rounded-lg transition-colors"
                        >
                          Ver <span className="ml-1 opacity-60">→</span>
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400 text-sm">
                      No se encontraron indicadores publicados para los filtros seleccionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && filteredIndicators.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#F8FAFC]">
              <div className="text-xs text-gray-500">
                Mostrando{' '}
                <span className="font-bold text-gray-800">
                  {(safeCurrentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                a{' '}
                <span className="font-bold text-gray-800">
                  {Math.min(safeCurrentPage * itemsPerPage, filteredIndicators.length)}
                </span>{' '}
                de <span className="font-bold text-gray-800">{filteredIndicators.length}</span>{' '}
                indicadores
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safeCurrentPage === 1}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>

                {visiblePageNumbers.map((pageItem) =>
                  typeof pageItem === 'number' ? (
                    <button
                      key={pageItem}
                      type="button"
                      onClick={() => setCurrentPage(pageItem)}
                      className={`min-w-8 rounded-lg px-2.5 py-1.5 text-xs font-bold transition ${
                        safeCurrentPage === pageItem
                          ? 'bg-[#002B5B] text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200/60'
                      }`}
                    >
                      {pageItem}
                    </button>
                  ) : (
                    <span key={pageItem} className="px-1 text-xs font-bold text-gray-400">
                      …
                    </span>
                  )
                )}

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safeCurrentPage === totalPages}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
